import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { UserStatus } from "../types"

import { RolesFilter } from "./RolesFilter"

import { StatusFilter } from "@/components/common/Filters"
import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import type { UserQueryableFields } from "@/graphql/codegen/graphql"
import { getStatusValue } from "@/utils/getStatusValue"

interface FilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: (open: boolean) => void
}

export type FormValues = Omit<UserQueryableFields, "isActive"> & {
  isActive: UserStatus | UserStatus[]
}

export const FilterDrawer: FC<FilterDrawerProps> = ({ toggleDrawer, showFilterDrawer }) => {
  const navigate = useNavigate({ from: "/app/admin/system-admin/users" })
  const { isActive, roleIds = [] } = useSearch({
    from: "/app/admin/system-admin/_layout/users",
    strict: true,
  })

  const methods = useForm<FormValues>({
    defaultValues: {
      isActive: UserStatus.Active,
      roleIds: [],
    },
    values: {
      isActive: getStatusValue(isActive, UserStatus),
      roleIds,
    },
    mode: "onChange",
  })

  const handleClearFilters = () => {
    methods.reset()
    navigate({
      search: (prev) => ({
        field: prev.field,
        order: prev.order,
        // temporary solution with setting a default value until we have instruction statuses,
        // and will have the possibility to set the default status in URL parameter
        isActive: true,
        roleIds: undefined,
        query: prev.query,
      }),
      replace: true,
    })
  }

  const handleFilterChange = (name: keyof FormValues, value: unknown) => {
    if (name === "isActive") {
      const isActiveValue =
        (value as UserStatus[]).length === 1 ? (value as UserStatus[])[0] === UserStatus.Active : undefined

      navigate({
        search: (prev) => ({
          field: prev.field,
          order: prev.order,
          isActive: isActiveValue,
          query: prev.query,
          roleIds: prev.roleIds,
        }),
        replace: true,
      })
    } else if (name === "roleIds") {
      navigate({
        search: (prev) => ({
          field: prev.field,
          order: prev.order,
          isActive: prev.isActive,
          query: prev.query,
          roleIds: value as string[],
        }),
        replace: true,
      })
    }
  }

  return (
    <SearchFilterDrawer
      toggleDrawer={() => toggleDrawer(!showFilterDrawer)}
      open={showFilterDrawer}
      clear={handleClearFilters}
    >
      <FormProvider {...methods}>
        <Box className='min-w-80 flex-none'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            User Status
          </Typography>
          <Divider />
          <StatusFilter
            name='isActive'
            data={Object.values(UserStatus)}
            control={methods.control}
            onFilterChange={handleFilterChange}
          />
        </Box>
        <Box className='grow overflow-hidden'>
          <RolesFilter onFilterChange={handleFilterChange} />
        </Box>
      </FormProvider>
    </SearchFilterDrawer>
  )
}
